export const CONTENT_ADMIN_TOKEN_KEY = 'content_admin_token';
export const CONTENT_ADMIN_REFRESH_TOKEN_KEY = 'content_admin_refresh_token';
export const CONTENT_ADMIN_TOKEN_CHANGED_EVENT = 'content-admin-token-changed';

// Admin roles that are authorized for all organizations
const PLATFORM_ADMIN_ROLES = ['PlatformAdmin', 'SuperAdmin'];
// Admin roles that are authorized for their own organization only
const ORG_ADMIN_ROLES = ['ContentEditor', 'OrganizationAdmin', 'Editor', 'Admin'];

/**
 * Decode a JWT payload without verification. Returns null for malformed tokens.
 * JWT format: header.payload.signature (each base64url).
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    // base64url → base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? b64 : b64 + '='.repeat(4 - (b64.length % 4));
    const json = atob(pad);
    return JSON.parse(
      decodeURIComponent(
        Array.prototype.map
          .call(json, (c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
    );
  } catch {
    return null;
  }
}

/**
 * Returns JWT expiry time in ms (Date.now() scale), or null if unknown.
 */
export function getJwtExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  if (typeof payload.exp !== 'number') return null;
  return payload.exp * 1000;
}

/**
 * Returns true if the token exists AND has a future `exp` claim.
 * A token without an `exp` claim is treated as invalid (we cannot trust it).
 */
export function isJwtValid(token) {
  const expMs = getJwtExpiryMs(token);
  if (expMs === null) return false;
  return expMs > Date.now();
}

/**
 * Extract the role claim(s) from a JWT. The backend may encode roles as:
 *   - "role" (string or array)
 *   - "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
 * Returns an array of role strings (empty if none found).
 */
export function getTokenRoles(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return [];
  const raw =
    payload.role ||
    payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
    payload.roles ||
    [];
  return Array.isArray(raw) ? raw : [raw];
}

/**
 * Extract the OrganizationId claim from a JWT.
 * Returns the string org ID, or null if not present.
 */
export function getTokenOrganizationId(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return payload.OrganizationId || payload.organizationId || payload.org_id || null;
}

/**
 * Returns true if the token carries a platform-level admin role
 * (authorized for any organization).
 */
export function isPlatformAdmin(token) {
  const roles = getTokenRoles(token);
  return roles.some((r) => PLATFORM_ADMIN_ROLES.includes(r));
}

/**
 * Returns true if the token is a valid admin token authorized for `orgId`.
 *   - PlatformAdmin tokens pass for any org.
 *   - Org-specific admin tokens pass only when their OrganizationId matches.
 *   - Expired or malformed tokens always fail.
 *   - Member/player tokens (no admin role) always fail.
 */
export function isAdminTokenAuthorizedForOrg(token, orgId) {
  if (!token || !isJwtValid(token)) return false;
  const roles = getTokenRoles(token);
  if (roles.some((r) => PLATFORM_ADMIN_ROLES.includes(r))) return true;
  if (!roles.some((r) => ORG_ADMIN_ROLES.includes(r))) return false;
  if (!orgId) return false;
  const tokenOrgId = getTokenOrganizationId(token);
  return String(tokenOrgId) === String(orgId);
}

export function notifyAdminTokenChanged() {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new Event(CONTENT_ADMIN_TOKEN_CHANGED_EVENT));
}

/**
 * Returns the stored content-admin JWT only if it is still valid.
 * Proactively removes the key from localStorage if the token is expired or malformed,
 * so consumers listening to the `storage` event will see the change.
 */
export function getValidContentAdminToken() {
  if (typeof window === 'undefined') return null;
  const token = window.localStorage.getItem(CONTENT_ADMIN_TOKEN_KEY);
  if (!token) return null;
  if (isJwtValid(token)) return token;
  // Token expired — clear it
  window.localStorage.removeItem(CONTENT_ADMIN_TOKEN_KEY);
  notifyAdminTokenChanged();
  // If no refresh token exists either, the session is truly lost
  if (!getAdminRefreshToken()) {
    notifyAdminSessionLost();
  }
  return null;
}

/**
 * Returns the stored admin JWT only when it is valid and authorized for orgId.
 */
export function getAuthorizedContentAdminToken(orgId) {
  const token = getValidContentAdminToken();
  if (!token) return null;
  return isAdminTokenAuthorizedForOrg(token, orgId) ? token : null;
}

/**
 * Reactive-safe boolean: true only if a valid (unexpired) admin token is present.
 * Side-effect: clears an expired token from storage on read.
 */
export function hasValidContentAdminToken() {
  return !!getValidContentAdminToken();
}

export function hasAuthorizedContentAdminToken(orgId) {
  return !!getAuthorizedContentAdminToken(orgId);
}

/**
 * Store admin access and (optionally) refresh tokens.
 */
export function storeAdminTokens(accessToken, refreshToken) {
  if (typeof window === 'undefined') return;
  if (accessToken) {
    window.localStorage.setItem(CONTENT_ADMIN_TOKEN_KEY, accessToken);
  }
  if (refreshToken) {
    window.localStorage.setItem(CONTENT_ADMIN_REFRESH_TOKEN_KEY, refreshToken);
  }
  notifyAdminTokenChanged();
}

/**
 * Clear all admin tokens from storage.
 */
export function clearAdminTokens() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(CONTENT_ADMIN_TOKEN_KEY);
  window.localStorage.removeItem(CONTENT_ADMIN_REFRESH_TOKEN_KEY);
  notifyAdminTokenChanged();
}

/**
 * Return the stored admin refresh token (no expiry check — refresh tokens
 * are validated server-side).
 */
export function getAdminRefreshToken() {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(CONTENT_ADMIN_REFRESH_TOKEN_KEY) || null;
}

// --- Admin Session / Expiry Callbacks ---

const adminTokenExpiryListeners = new Set();

/**
 * Register a callback that fires when the admin session is completely lost
 * (no valid access token AND no refresh token to restore it).
 * Returns an unsubscribe function.
 */
export function onAdminTokenInvalid(callback) {
  adminTokenExpiryListeners.add(callback);
  return () => adminTokenExpiryListeners.delete(callback);
}

/** Internal: notify all registered listeners that the admin session is lost. */
function notifyAdminSessionLost() {
  adminTokenExpiryListeners.forEach(cb => { try { cb(); } catch {} });
}

/**
 * Returns true if there's any possibility of restoring an admin session:
 * - A valid (unexpired) access token exists, OR
 * - A refresh token exists (which could be used to get a new access token)
 */
export function isAdminSessionPotentiallyActive() {
  if (typeof window === 'undefined') return false;
  if (getValidContentAdminToken()) return true;
  return !!getAdminRefreshToken();
}
